// ข้อสอบหนึ่งชุด — กรอกคำตอบ / ผลสอบ / วิเคราะห์ข้อสอบ / ส่งเข้าตารางคะแนน
//
// ทุกแท็บอ่านจาก GET /exams/:id/results ก้อนเดียว (คะแนนคิดจากเฉลยปัจจุบันฝั่ง server)
// บันทึกคำตอบหรือแก้เฉลยแล้วโหลดก้อนนี้ใหม่ ตัวเลขทุกแท็บจึงตรงกันเสมอ
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Badge, Button, ButtonGroup, Card, Dropdown, Nav } from 'react-bootstrap'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import Header from '../layouts/Header'
import Footer from '../layouts/Footer'
import axiosInstance from '../services/axiosInterceptor'
import { useLoading } from '../contexts/LoadingContext'
import { API, fmt, roomLabel, showError } from './examUtils'
import ExamEntry from './ExamEntry'
import ExamResults from './ExamResults'
import ExamAnalysis from './ExamAnalysis'
import ExamPush from './ExamPush'

const ALL_ROOMS = 'all'

const TABS = [
  { key: 'entry', label: 'กรอกคำตอบ', icon: 'ri-edit-box-line' },
  { key: 'results', label: 'ผลสอบ', icon: 'ri-list-ordered' },
  { key: 'analysis', label: 'วิเคราะห์ข้อสอบ', icon: 'ri-bar-chart-2-line' },
  { key: 'push', label: 'ส่งเข้าตารางคะแนน', icon: 'ri-upload-2-line' },
]

export default function ExamDetail() {
  const { examId } = useParams()
  const { setLoading } = useLoading()
  const [, setSkin] = useState('')
  const [params, setParams] = useSearchParams()
  const tab = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'entry'
  const [data, setData] = useState(null)
  // teach_id ที่เลือก หรือ ALL_ROOMS (เฉพาะผลสอบ/วิเคราะห์) — null = ยังไม่ได้เลือก ใช้ห้องแรก
  // แยก "ทุกห้อง" ออกจาก "ยังไม่เลือก" ไม่งั้นโหลดใหม่ (เช่น แก้เฉลยจากแท็บวิเคราะห์) แล้วเด้งกลับไปห้องแรก
  const [roomId, setRoomId] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    return axiosInstance.get(`${API}/exams/${examId}/results`)
      .then((res) => {
        setData(res.data)
      })
      .catch((err) => showError(err, 'โหลดข้อสอบไม่สำเร็จ'))
      .finally(() => setLoading(false))
  }, [examId, setLoading])

  useEffect(() => { load() }, [load])

  const setTab = (key) => setParams((p) => {
    const next = new URLSearchParams(p)
    next.set('tab', key)
    return next
  }, { replace: true })

  // แท็บที่ทำงานรายห้อง (กรอกคำตอบ / ส่งคะแนน) ต้องมีห้องที่เลือกเสมอ
  const needsRoom = tab === 'entry' || tab === 'push'
  const firstRoom = data?.rooms[0]?.teach_id ?? null
  // ห้องที่เลือกไว้อาจถูกถอดออกจากข้อสอบไปแล้ว (แก้ห้องในหน้าแก้ไข) — ถอยไปห้องแรก ไม่ใช่หน้าว่าง
  const pickedRoom = data?.rooms.some((r) => r.teach_id === roomId) ? roomId : firstRoom
  const effectiveRoom = !needsRoom && roomId === ALL_ROOMS ? null : pickedRoom
  const room = data?.rooms.find((r) => r.teach_id === effectiveRoom) || null

  const students = useMemo(() => {
    if (!data) return []
    return effectiveRoom ? data.students.filter((s) => s.teach_id === effectiveRoom) : data.students
  }, [data, effectiveRoom])

  if (!data) {
    return (
      <>
        <Header onSkin={setSkin} />
        <div className="main main-app p-2 p-lg-4" />
      </>
    )
  }

  const { exam, rooms } = data
  const first = rooms[0]
  const answered = data.students.filter((s) => s.score).length

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-2 p-lg-4">
        <div className="d-flex flex-wrap align-items-start gap-2 mb-3">
          <div className="me-auto">
            <Link to="/teaching/exams" className="fs-sm"><i className="ri-arrow-left-line"></i> ข้อสอบของฉัน</Link>
            <h4 className="main-title mb-0">{exam.exam_name}</h4>
            <div className="fs-sm text-secondary">
              {first && `${first.sub_subid} ${first.sub_subname} · ภาคเรียนที่ ${first.sub_semester}/${first.year_year} · `}
              {data.questions.length} ข้อ · เต็ม {fmt(data.total_points)} คะแนน · กรอกคำตอบแล้ว {answered} คน
            </div>
          </div>
          <Dropdown>
            <Dropdown.Toggle size="sm" variant="outline-primary" id="exam-print">
              <i className="ri-printer-line"></i> พิมพ์กระดาษคำตอบ
            </Dropdown.Toggle>
            <Dropdown.Menu align="end">
              {rooms.map((r) => (
                <Dropdown.Item key={r.teach_id} as={Link} to={`/teaching/exams/${examId}/print/${r.teach_id}`}>
                  ห้อง {roomLabel(r)} ({r.student_count} คน)
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Button as={Link} to={`/teaching/exams/${examId}/scan`} size="sm" variant="primary">
            <i className="ri-scan-2-line"></i> สแกนกระดาษคำตอบ
          </Button>
          <Button as={Link} to={`/teaching/exams/${examId}/edit`} size="sm" variant="outline-secondary">
            <i className="ri-edit-line"></i> แก้ไขข้อสอบ/เฉลย
          </Button>
        </div>

        <Card className="card-one">
          <Card.Header className="d-flex flex-wrap align-items-center gap-2">
            <Nav variant="pills" className="me-auto flex-nowrap overflow-auto" activeKey={tab} onSelect={setTab}>
              {TABS.map((t) => (
                <Nav.Item key={t.key}>
                  <Nav.Link eventKey={t.key} className="text-nowrap py-1 px-2">
                    <i className={`${t.icon} me-1`}></i>{t.label}
                  </Nav.Link>
                </Nav.Item>
              ))}
            </Nav>
            <ButtonGroup size="sm" className="flex-wrap">
              {!needsRoom && rooms.length > 1 && (
                <Button variant={effectiveRoom === null ? 'primary' : 'outline-primary'} onClick={() => setRoomId(ALL_ROOMS)}>
                  ทุกห้อง
                </Button>
              )}
              {rooms.map((r) => (
                <Button key={r.teach_id} variant={effectiveRoom === r.teach_id ? 'primary' : 'outline-primary'}
                  onClick={() => setRoomId(r.teach_id)}>
                  {roomLabel(r)}
                  {r.response_count > 0 && <Badge bg="light" text="dark" className="ms-1">{r.response_count}</Badge>}
                </Button>
              ))}
            </ButtonGroup>
          </Card.Header>
          <Card.Body>
            {tab === 'entry' && room && (
              <ExamEntry key={room.teach_id} examId={examId} exam={exam} questions={data.questions}
                room={room} students={students} onSaved={load} />
            )}
            {tab === 'results' && (
              <ExamResults exam={exam} questions={data.questions} students={students}
                showRoom={effectiveRoom === null} totalPoints={data.total_points} />
            )}
            {tab === 'analysis' && (
              <ExamAnalysis examId={examId} exam={exam} questions={data.questions}
                teachId={effectiveRoom} version={data} onChanged={load} />
            )}
            {tab === 'push' && room && (
              <ExamPush key={room.teach_id} examId={examId} room={room} students={students}
                totalPoints={data.total_points} onPushed={load} />
            )}
          </Card.Body>
        </Card>
        <Footer />
      </div>
    </>
  )
}
